import LogoBlueByte from '@assets/icons/blue_byte_video_demo_logo.svg';
import Chevron from '@assets/icons/chevron-back-sider.svg';
import Minimize from '@assets/icons/learning-overview/minimize.svg';
import BackActive from '@assets/icons/learning-overview/video-player/back-active.svg';
import Check from '@assets/icons/learning-overview/video-player/check.svg';
import NextDisable from '@assets/icons/learning-overview/video-player/next-disable.svg';
import PauseBig from '@assets/icons/learning-overview/video-player/pause-big.svg';
import PauseSmall from '@assets/icons/learning-overview/video-player/pause-small.svg';
import PlayBig from '@assets/icons/learning-overview/video-player/play-big.svg';
import PlaySmall from '@assets/icons/learning-overview/video-player/play-small.svg';
import { BaseDivider } from '@components/divider';
import { BODY_MD_BOLD_STYLES, BODY_SM_SM_MEDIUM_STYLES } from '@constants/text.ts';
import styled, { css } from 'styled-components';

interface SeekSliderProps {
    $played: number;
    $watched: number;
    $isScrubbing: boolean;
}

interface SmallButtonProps {
    $isPreviewView: boolean;
}

const GRAY_GRADIENT_COVER =
    'linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.2) 100%), var(--colors-overlay-grey-light, rgba(112, 118, 141, 0.2))';

export const PlayerContainer = styled.div<{ $isFullScreen?: boolean }>`
    width: 100%;
    aspect-ratio: 16 / 9;
    position: relative;
    cursor: pointer;

    ${({ $isFullScreen }) =>
        $isFullScreen &&
        css`
            aspect-ratio: auto;
            width: 100vw;
            height: 100vh;
        `}
`;

export const VideoArea = styled.div<{ $isFullScreen?: boolean }>`
    border-radius: 8px;
    overflow: hidden;
    position: absolute;
    inset: 0;

    & .react-player__preview,
    & video,
    & youtube-video {
        border-radius: inherit !important;
        object-fit: ${({ $isFullScreen }) => ($isFullScreen ? 'contain' : 'cover')};
        width: 100% !important;
        height: 100% !important;
    }
`;

const BigButtonsStyles = css`
    width: 94px;
    height: 96px;
`;
export const BigPlayButton = styled(PlayBig)`
    ${BigButtonsStyles}
`;

export const BigPauseButton = styled(PauseBig)`
    ${BigButtonsStyles}
`;

export const BigButtonWrapper = styled.div<{ $visible: boolean }>`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;

    opacity: ${({ $visible }) => ($visible ? 1 : 0)};
    visibility: ${({ $visible }) => ($visible ? 'visible' : 'hidden')};
    pointer-events: ${({ $visible }) => ($visible ? 'auto' : 'none')};
    transition:
        opacity 0.3s ease,
        visibility 0.3s ease;
`;

export const ControlsOverlay = styled.div`
    position: absolute;
    bottom: 24px;
    left: 24px;
    right: 24px;
    padding: 10px 16px;
    display: flex;
    align-items: center;
    gap: 16px;
    background-color: rgba(44, 44, 44, 0.1);
    border-radius: 16px;
`;

export const ControlGroup = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`;

const SmallButtonsStyles = css<SmallButtonProps>`
    width: ${({ $isPreviewView }) => ($isPreviewView ? '16px' : '40px')};
    height: ${({ $isPreviewView }) => ($isPreviewView ? '16px' : '40px')};
    background: var(--colors-text-on-action, #fff);
    border-radius: 50%;
`;

export const PlaySmallButton = styled(PlaySmall)<SmallButtonProps>`
    ${SmallButtonsStyles};
    padding: ${({ $isPreviewView }) => ($isPreviewView ? '12px' : '11px')};
`;

export const PauseSmallButton = styled(PauseSmall)<SmallButtonProps>`
    ${SmallButtonsStyles};
    padding: ${({ $isPreviewView }) => ($isPreviewView ? '12px' : '6px')};
`;

export const SeekWrapper = styled.div`
    flex: 1;
    display: flex;
    align-items: center;
`;

export const TimeLabel = styled.span`
    color: var(--colors-text-on-action, #fff);
    margin-left: 8px;
`;

export const CheckIcon = styled(Check)`
    position: absolute;
    left: 8px;
    top: 50%;
    transform: translateY(-50%);
`;

export const BaseDividerWrapper = styled(BaseDivider)`
    margin: 0;
    border-top-color: rgba(255, 255, 255, 0.1) !important;
`;

export const SettingsPanel = styled.div`
    position: absolute;
    bottom: 90px;
    right: 80px;
    width: 310px;
    display: flex;
    flex-direction: column;
    gap: var(--spacing-xs, 8px);
    padding: var(--spacing-md, 24px);
    background-color: rgba(44, 44, 44, 0.75);
    border-radius: 8px;
    align-items: flex-start;
`;

export const SettingsRow = styled.div<{ isBack?: boolean; isSubMenu?: boolean }>`
    align-items: center;
    border-radius: var(--border-radius-xs_4, 4px);
    position: relative;
    display: flex;
    justify-content: ${({ isSubMenu }) => (isSubMenu ? 'flex-start' : 'space-between')};
    padding: ${({ isBack }) => (isBack ? '10px' : '4px')}
        ${({ isSubMenu, isBack }) => (isSubMenu ? `${isBack ? '1px' : '34px'}` : '8px')};

    width: 262px;
    cursor: pointer;
    gap: ${({ isSubMenu }) => (isSubMenu ? '12px' : '4px')};
    height: ${({ isBack }) => (isBack ? 'auto' : '32px')};

    &:hover {
        background-color: ${({ isBack }) => (isBack ? 'transparent' : '#ffffff1a')};
    }
`;

export const SettingsRowOptions = styled.div`
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: '2px';
`;

export const BackDisableButton = styled(NextDisable)`
    transform: rotate(180deg);
`;

export const NextActiveButton = styled(BackActive)`
    transform: rotate(180deg);
`;

export const IconWrapper = styled.div<{ disabled?: boolean }>`
    display: flex;
    width: 40px;
    height: 40px;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    color: var(--colors-text-on-action, #fff);
    opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};
    cursor: ${({ disabled }) => (disabled ? 'default' : 'pointer')};
    transition:
        background-color 150ms,
        color 150ms;
    &:hover {
        background-color: ${({ disabled }) => (disabled ? 'transparent' : 'rgba(255, 255, 255, 0.2)')};
    }
`;

export const Frame = styled.div`
    align-items: center;
    background-color: rgba(44, 44, 44, 0.75);
    border-radius: var(--border-radius-s_8);
    display: flex;
    gap: 10px;
    height: 92px;
    justify-content: center;
    position: absolute;
    top: -110px;
    padding: var(--spacing-xs) 13px;
`;

export const Label = styled.div`
    color: var(--colors-text-on-action);
    ${BODY_SM_SM_MEDIUM_STYLES};
    white-space: nowrap;
`;

export const TextWrapper = styled.div`
    color: var(--colors-text-on-action);
    ${BODY_SM_SM_MEDIUM_STYLES};
    white-space: nowrap;
    margin-top: -1px;
`;

export const ChevronIcon = styled(Chevron)<{ $rotated?: boolean }>`
    width: 20px;
    height: 20px;
    transform: rotate(${({ $rotated }) => ($rotated ? '0deg' : '180deg')});
`;

export const SubtitlesSwitch = styled.div<{ isOn: boolean }>`
    background-color: var(--colors-surface-action, #2171c6);
    border: 1px solid var(--colors-border-action, #2171c6);
    border-radius: var(--border-radius-round, 48px);
    height: 26px;
    width: 48px;
    padding: 2px;
    display: flex;
    align-items: center;
    cursor: pointer;
    transition:
        background-color 0.2s,
        border-color 0.2s;

    &:hover {
        background-color: var(--colors-border-action-hover, #1a5ca2);
        border-color: var(--colors-border-action-hover, #1a5ca2);
    }

    .knob {
        background-color: var(--colors-surface-primary, #fff);
        border-radius: 50%;
        height: 20px;
        width: 20px;
        transform: translateX(${({ isOn }) => (isOn ? '22px' : '0px')});
        transition: transform 0.2s;
    }
`;

export const VolumeContainer = styled.div`
    position: relative;
    display: inline-block;
`;

const volumeSliderRunnableTrackStyles = css`
    height: 5px;
    border-radius: 100px;
    background-color: var(--neutral-350, #b4b8bf);
`;
export const VolumeSlider = styled.input.attrs({
    type: 'range',
    min: 0,
    max: 1,
    step: 0.01,
})`
    width: 70px;
    height: 5px;
    margin: 0 -26.5px;
    transform: rotate(-90deg);
    transform-origin: center;

    -webkit-appearance: none;
    background: transparent;

    &::-webkit-slider-runnable-track {
        ${volumeSliderRunnableTrackStyles};
    }
    &::-moz-range-track {
        ${volumeSliderRunnableTrackStyles};
    }

    &::-webkit-slider-thumb {
        -webkit-appearance: none;
        width: 17px;
        height: 17px;
        margin-top: -6px;
        border-radius: 8.5px;
        background-color: var(--colors-icons-on-action, #fff);
        cursor: pointer;
    }
    &::-moz-range-thumb {
        width: 17px;
        height: 17px;
        border-radius: 8.5px;
        background-color: var(--colors-icons-on-action, #fff);
        cursor: pointer;
        border: none;
    }

    &:hover::-webkit-slider-thumb,
    &:hover::-moz-range-thumb {
        transform: scale(1.1);
    }

    &:focus {
        outline: none;
    }
`;

const seekSliderRunnableTrackStyles = css`
    height: 8px;
    border-radius: 4px;
    background-color: rgba(255, 255, 255, 0.3);
`;
export const SeekSlider = styled.input.attrs({
    type: 'range' as const,
    min: 0,
    max: 1,
    step: 0.001,
})<SeekSliderProps>`
    width: 86%;
    height: 8px;
    margin: 0;

    background: ${({ $played, $watched, $isScrubbing }) => {
        const fillColor = $isScrubbing ? 'var(--colors-surface-action)' : 'var(--colors-text-on-action, #fff)';
        const playedStop = `${($played * 100).toFixed(3)}%`;
        const watchedStop = `${($watched * 100).toFixed(3)}%`;
        return `
      ${
          $isScrubbing
              ? `
        linear-gradient(
          to right,
          ${fillColor} 0%,
          ${fillColor} ${playedStop},
          transparent ${playedStop},
          transparent 100%
        ),
      `
              : ''
      }
      linear-gradient(
        to right,
        var(--colors-text-on-action, #fff) 0%,
        var(--colors-text-on-action, #fff) ${watchedStop},
        transparent ${watchedStop},
        transparent 100%
      )
    `;
    }};
    border-radius: 4px;
    -webkit-appearance: none;

    &::-webkit-slider-thumb {
        -webkit-appearance: none;
        width: ${({ $isScrubbing }) => ($isScrubbing ? '20px' : '16px')};
        height: ${({ $isScrubbing }) => ($isScrubbing ? '20px' : '16px')};
        margin-top: ${({ $isScrubbing }) => ($isScrubbing ? '-6px' : '-4px')};
        background-color: ${({ $isScrubbing }) =>
            $isScrubbing ? 'var(--colors-surface-action)' : 'var(--colors-text-on-action, #fff)'};
        cursor: pointer;
        border-radius: 50%;
        border: none;
        transition:
            transform 0.1s,
            width 0.1s,
            height 0.1s,
            margin-top 0.1s;
    }

    &::-webkit-slider-runnable-track {
        ${seekSliderRunnableTrackStyles};
    }

    &::-moz-range-track {
        ${seekSliderRunnableTrackStyles};
    }

    &::-moz-range-progress {
        height: 8px;
        border-radius: 4px;
        background-color: ${({ $isScrubbing }) => ($isScrubbing ? 'var(--colors-surface-action)' : 'transparent')};
    }

    &::-moz-range-thumb {
        width: ${({ $isScrubbing }) => ($isScrubbing ? '20px' : '16px')};
        height: ${({ $isScrubbing }) => ($isScrubbing ? '20px' : '16px')};
        margin-top: ${({ $isScrubbing }) => ($isScrubbing ? '-6px' : '-4px')};

        background-color: ${({ $isScrubbing }) =>
            $isScrubbing ? 'var(--colors-surface-action)' : 'var(--colors-text-on-action, #fff)'};
        cursor: pointer;
        border-radius: 50%;
        border: none;
        transition:
            transform 0.1s,
            width 0.1s,
            height 0.1s;
    }

    &:focus {
        outline: none;
    }
`;

export const WatermarkContainer = styled.div`
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: var(--spacing-2xs);
    padding: var(--spacing-xs);
    width: fit-content;
    background-color: rgba(44, 44, 44, 0.5);
    border-radius: var(--border-radius-s_8);
`;

export const WatermarkLogo = styled(LogoBlueByte)`
    width: 83px;
    height: 24px;
`;

export const WatermarkMeta = styled.div`
    white-space: nowrap;
    width: fit-content;
    color: #ffffff;
    ${BODY_MD_BOLD_STYLES};
`;

export const MinimizeIcon = styled(Minimize)`
    width: 20px;
    height: 20px;

    path {
        stroke: white;
    }
`;

export const VideoClickOverlay = styled.div<{ $showFallbackBackground: boolean; $cover: string | null }>`
    position: absolute;
    inset: 0;
    cursor: pointer;
    background: ${({ $showFallbackBackground, $cover }) =>
        $showFallbackBackground ? ($cover ? `url(${$cover})` : GRAY_GRADIENT_COVER) : 'none'};
    background-size: ${({ $showFallbackBackground, $cover }) =>
        $showFallbackBackground && $cover ? 'cover' : '100% 100%'};
    background-repeat: no-repeat;
    background-position: center center;
`;
